/**
 * Cat & Mouse — Hangul jamo arithmetic (specs/cat-mouse-mode.md §9.3, §7.3).
 *
 * Korean is the one language in this mode whose class membership is a *predicate* rather than a
 * list: class `ㄱ` is the bare jamo PLUS every syllable block built on it — 가 고 구 기 개 그 게 …
 * There are 11 172 of those, so they are computed, never authored.
 *
 * **The trap this file exists for.** The jamo a child sees on a chart and on a keyboard is a
 * *compatibility* jamo — `ㄱ` is U+3131. The jamo *inside* a syllable block is a *conjoining* jamo
 * — the initial `ᄀ` is U+1100. They are different codepoints and they do not compare equal:
 *
 *     '가'.normalize('NFD').includes('ㄱ')   // false
 *
 * …which looks like a data bug for an afternoon. So membership goes through the arithmetic below
 * plus the compat→index tables, and never through string containment.
 *
 * Phaser-free, so it can be tested in node like `game/maze/segment.ts`.
 */

/** First precomposed syllable, 가 (U+AC00). */
const BASE = 0xac00;
const N_MEDIAL = 21;
const N_FINAL = 28;
/** 19 × 21 × 28 — the whole Hangul Syllables block. */
const N_SYLLABLE = 11172;

/**
 * The three jamo series, in Unicode's own order, written as **compatibility** jamo — the forms a
 * pack file, a door and a keyboard all use. Index into these is exactly the index the syllable
 * arithmetic below produces, which is what makes the tables a pair of `indexOf` calls rather than
 * 51 hand-written entries that can drift.
 */
export const INITIALS = [...'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ'];
export const MEDIALS = [...'ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ'];
/** Index 0 is "no batchim" and has no glyph. */
export const FINALS = ['', ...'ㄱㄲㄳㄴㄵㄶㄷㄹㄺㄻㄼㄽㄾㄿㅀㅁㅂㅄㅅㅆㅇㅈㅊㅋㅌㅍㅎ'];

export interface Jamo {
  /** Index into INITIALS. */
  initial: number;
  /** Index into MEDIALS. */
  medial: number;
  /** Index into FINALS; 0 = no batchim. */
  final: number;
}

/**
 * The jamo of one precomposed syllable block, or `null` if the string does not start with one —
 * a bare jamo, a Latin letter and an empty string all return null rather than throwing.
 *
 * Only the FIRST code point is read: a cell holds one grapheme cluster, and a cluster that is a
 * Hangul block is a single code point.
 */
export function jamoOf(block: string): Jamo | null {
  const c = block.codePointAt(0);
  if (c === undefined) return null;
  const i = c - BASE;
  if (i < 0 || i >= N_SYLLABLE) return null;
  return {
    initial: Math.floor(i / (N_MEDIAL * N_FINAL)),
    medial: Math.floor(i / N_FINAL) % N_MEDIAL,
    final: i % N_FINAL,
  };
}

/**
 * The reverse — what a picture door does in Korean (§7.3). `compose(0, 8, 16)` is `곰`.
 *
 * Watching ㄱ + ㅗ + ㅁ turn into 곰 in front of the child is not decoration: it is what Hangul *is*,
 * it is how the script is taught in every Korean first grade, and no other language in this mode
 * can show it.
 */
export function compose(initial: number, medial: number, final = 0): string {
  return String.fromCodePoint(BASE + (initial * N_MEDIAL + medial) * N_FINAL + final);
}

/** Index of a compatibility jamo in INITIALS, or -1 if it is not an initial-capable consonant. */
export function initialIndex(compatJamo: string): number {
  return INITIALS.indexOf(compatJamo);
}

/** Index of a compatibility jamo in MEDIALS, or -1 if it is not a vowel. */
export function medialIndex(compatJamo: string): number {
  return MEDIALS.indexOf(compatJamo);
}

/**
 * Does `glyph` — a bare jamo or a syllable block — contain `compatJamo` in the named position?
 *
 * `'initial'` matches 가 for ㄱ; `'medial'` matches 가 for ㅏ. A bare jamo matches itself in either
 * position, which is what puts the chart form and the blocks in one class.
 *
 * v1 never asks about finals (받침): a ㄴ-final board would put the ㄴ in a position the door's own
 * glyph does not visually occupy. `FINALS` is exported anyway — the batchim variant is the same
 * scene with this predicate swapped, and it is the sequel multilingual-korean.md §3.3 wants.
 */
export function hasJamo(glyph: string, compatJamo: string, position: 'initial' | 'medial'): boolean {
  if (glyph === compatJamo) return true;
  const j = jamoOf(glyph);
  if (!j) return false;
  return position === 'initial'
    ? j.initial === initialIndex(compatJamo)
    : j.medial === medialIndex(compatJamo);
}

/**
 * Syllable blocks that belong to a consonant class, in teaching order — `ㄱ` → 가 고 구 기 그 …
 *
 * Generated rather than authored: enumerating the blocks of 14 consonants by hand is 140 entries
 * of data that Unicode already knows. The medials are the six a Korean first-grader meets first
 * (ㅏ ㅗ ㅜ ㅣ ㅡ ㅓ) and every block produced is a real, pronounceable syllable.
 */
export function blocksForInitial(compatJamo: string, count = 5): string[] {
  const i = initialIndex(compatJamo);
  if (i < 0) return [];
  const medials = ['ㅏ', 'ㅗ', 'ㅜ', 'ㅣ', 'ㅡ', 'ㅓ'];
  return medials.slice(0, count).map((m) => compose(i, medialIndex(m)));
}

/**
 * The mirror for a vowel class — `ㅏ` → 가 나 다 마 바 …, blocks that all carry the same medial.
 * The initials are the five most frequent, so the board stays made of syllables a child has seen.
 */
export function blocksForMedial(compatJamo: string, count = 5): string[] {
  const m = medialIndex(compatJamo);
  if (m < 0) return [];
  const initials = ['ㄱ', 'ㄴ', 'ㄷ', 'ㅁ', 'ㅂ', 'ㅅ'];
  return initials.slice(0, count).map((c) => compose(initialIndex(c), m));
}
